import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, X, TrendingUp, Layers, Search,Globe, BarChart3, MapPin, Repeat, Briefcase } from "lucide-react";
import BrandMarquee from "@/components/BrandMarquee";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import SectionLabel from "@/components/SectionLabel";
import ParallaxImage from "@/components/ParallaxImage";
import { BRANDS_FAQS, INDUSTRIES, IMAGES } from "@/data/content";
import { AUTH_LINKS } from "@/data/auth-links";

const BENEFITS = [
  { Icon: Search, t: "Strategic Influencer Discovery", d: "Identify and collaborate with verified influencers who align with your brand's audience, industry, and campaign goals — across niches, languages and regions." },
  { Icon: Layers, t: "Smarter Campaign Management", d: "Plan and coordinate influencer campaigns through structured workflows that simplify creator collaboration, content planning and execution." },
  { Icon: BarChart3, t: "Real-Time Campaign Insights", d: "Track engagement, reach, and performance with transparent reporting and analytics that connect influence to objectives." },
  { Icon: MapPin, t: "Regional & Hyperlocal Access", d: "Reach diverse audiences via regional and hyperlocal influencers across Tamil, Telugu, Malayalam, Kannada and more." },
  { Icon: Repeat, t: "Scalable Collaborations", d: "Build long-term creator partnerships and scale influencer programs without compromising on quality or brand alignment." },
  { Icon: Globe, t: "Global Reach", d: "Collaborate with creators from across the globe and reach audiences in different languages and regions." },
];

const COMPARE = [
  { challenge: "Finding the right influencers for campaigns", incer: "Discover verified creators with audience insights and niche relevance" },
  { challenge: "Limited access to regional influencers", incer: "Connect with regional and hyperlocal creators across markets" },
  { challenge: "Managing multiple influencer collaborations", incer: "Coordinate campaigns through structured workflows and campaign support" },
  { challenge: "UGC and influencer campaigns require separate tools", incer: "Run UGC and influencer campaigns in one platform" },
  { challenge: "Lack of visibility into campaign performance", incer: "Track campaigns with real-time insights and analytics" },
  { challenge: "Influencer tools are fragmented", incer: "Manage discovery, campaigns, and insights in one platform" },
  { challenge: "Hidden costs and extra add-ons", incer: "Transparent solutions without unnecessary add-ons" },
];

const STRATEGIC_POINTS = [
  "Reach targeted audiences with authentic creator content",
  "Build trust through credible creator partnerships",
  "Improve campaign effectiveness through measurable insights",
  "Expand brand presence across regional and digital communities",
];

export default function Brands() {
  return (
    <div data-testid="brands-page" className="bg-[#050505] text-[#F3E8E0]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden grain-bg">
        <div className="absolute top-32 right-1/4 w-[500px] h-[500px] bg-[#FF4A00] opacity-10 rounded-full blur-[160px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-7"
            >
              <SectionLabel className="mb-6">For Brands</SectionLabel>
              <h1
                data-testid="brands-hero-title"
                className="font-display text-4xl md:text-6xl lg:text-[6.5rem] font-semibold tracking-tighter leading-[0.92]"
              >
                The Creator Platform Trusted by{" "}
                <span className="text-[#FF4A00] italic font-medium">Leading Brands.</span>
              </h1>
              <p className="mt-8 text-lg text-[#A3A3A3] leading-relaxed max-w-xl">
                Incer empowers brands to discover verified influencers, run strategic creator campaigns,
                and track results — through an influencer marketing platform built for modern brand growth.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={AUTH_LINKS.brandSignUp}
                  data-testid="brands-cta-signup"
                  className="inline-flex items-center gap-2 bg-[#FF4A00] text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-[#E64300] transition-all active:scale-95"
                >
                  Sign up as a Brand <Briefcase className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5"
            >
              <div className="relative aspect-square rounded-3xl overflow-hidden border border-[#1a1a1a]">
                <ParallaxImage
                  src={IMAGES.brand}
                  alt="Creators collaborating with brands through Incer"
                  className="absolute inset-0"
                  intensity={0.12}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 via-transparent to-transparent z-10" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-12 border-y border-[#1a1a1a] bg-[#070707]">
        <BrandMarquee label="Trusted by ambitious brands" />
      </section>

      {/* BENEFITS */}
      <section className="py-24 md:py-32" data-testid="brand-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <SectionLabel className="mb-5">What you get</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              Everything you need to run influence at scale.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-glow group p-8 md:p-10 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00]/40 transition-colors"
                data-testid={`brand-benefit-${i}`}
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl bg-[#FF4A00]/10 flex items-center justify-center shrink-0 group-hover:bg-[#FF4A00]/20 transition-colors">
                    <b.Icon className="w-6 h-6 text-[#FF4A00]" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-medium tracking-tight mb-2">{b.t}</h3>
                    <p className="text-[#A3A3A3] leading-relaxed">{b.d}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a] bg-[#070707]" data-testid="brand-compare">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <SectionLabel className="mb-5">How Incer wins</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              The old way <span className="text-[#A3A3A3]">vs.</span> the Incer way.
            </h2>
          </div>

          <div className="rounded-2xl border border-[#1a1a1a] overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 bg-[#0a0a0a] border-b border-[#1a1a1a]">
              <div className="p-6 md:p-8 flex items-center gap-3 text-[#A3A3A3] uppercase tracking-widest text-xs font-semibold">
                <X className="w-4 h-4" /> Industry Challenge
              </div>
              <div className="p-6 md:p-8 flex items-center gap-3 text-[#FF4A00] uppercase tracking-widest text-xs font-semibold border-l border-[#1a1a1a]">
                <Check className="w-4 h-4" /> How Incer Solves It
              </div>
            </div>
            {COMPARE.map((row, i) => (
              <div
                key={i}
                data-testid={`compare-row-${i}`}
                className="grid grid-cols-1 md:grid-cols-2 border-b border-[#1a1a1a] last:border-b-0 hover:bg-[#0d0d0d] transition-colors"
              >
                <div className="p-6 md:p-8 text-[#A3A3A3] text-base md:text-lg">{row.challenge}</div>
                <div className="p-6 md:p-8 text-[#F3E8E0] text-base md:text-lg border-l border-[#1a1a1a] font-medium">
                  {row.incer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STRATEGIC IM MATTERS */}
      <section className="py-24 md:py-32" data-testid="brand-strategic">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <SectionLabel className="mb-5">Why it matters</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
                Strategic influence drives <span className="italic text-[#FF4A00]">measurable</span> brand outcomes.
              </h2>
              <p className="mt-8 text-lg text-[#A3A3A3] leading-relaxed">
                Influencer marketing has become one of the most powerful channels for building brand
                awareness and driving purchase decisions. But success requires more than partnering with
                creators — it requires strategy, audience alignment and performance insights.
              </p>
              <ul className="mt-10 space-y-4">
                {STRATEGIC_POINTS.map((p, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`strategic-point-${i}`}>
                    <TrendingUp className="w-5 h-5 text-[#FF4A00] mt-1 shrink-0" />
                    <span className="text-[#F3E8E0] text-lg">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2 relative">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#1a1a1a]">
                <ParallaxImage
                  src={IMAGES.brandStrategic}
                  alt="Creator reviewing social media analytics and campaign performance"
                  className="absolute inset-0"
                  intensity={0.12}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10" />
                <div className="absolute bottom-8 left-8 right-8 z-20">
                  <div className="label-eyebrow mb-2">In focus</div>
                  <div className="font-display text-2xl md:text-3xl font-medium">
                    Verified creators. Real audiences. Honest reach.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-32 border-t border-[#1a1a1a] bg-[#070707]" data-testid="brand-industries">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-16 max-w-3xl">
            <SectionLabel className="mb-5">Industries</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
              Industries we work with.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {INDUSTRIES.map((ind, i) => (
              <div
                key={ind.name}
                data-testid={`brand-industry-${i}`}
                className="p-6 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00] transition-colors"
              >
                <ind.Icon className="w-5 h-5 text-[#FF4A00] mb-4" />
                <div className="font-display text-lg md:text-xl font-medium tracking-tight">{ind.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        testId="brand-cta"
        eyebrow="Let's build influence"
        title="Run smarter campaigns. With creators that move audiences."
        description="Tell us about your brand. We'll match you with the right creators and design a campaign that delivers."
      />
      <FAQSection items={BRANDS_FAQS} testId="brand-faq" />
    </div>
  );
}
